import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import './stylesheets/Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
  refreshPendingBugs?: React.MutableRefObject<(() => void) | null>;
}

const Layout: React.FC<LayoutProps> = ({ children, currentPage, onPageChange, refreshPendingBugs }) => {
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasPendingBugs, setHasPendingBugs] = useState(false);

  const checkPendingBugs = async () => {
    try {
      const bugsQuery = query(
        collection(db, 'reported_bugs'),
        where('completed', '==', false)
      );
      const snapshot = await getDocs(bugsQuery);
      setHasPendingBugs(!snapshot.empty);
    } catch (error) {
      console.error('Error checking pending bugs:', error);
    }
  };

  useEffect(() => {
    checkPendingBugs();
    // Check every 30 seconds for new pending bugs
    const interval = setInterval(checkPendingBugs, 30000);

    return () => clearInterval(interval);
  }, []);

  // Expose the refresh function to parent component
  useEffect(() => {
    if (refreshPendingBugs) {
      refreshPendingBugs.current = checkPendingBugs;
    }
  }, [refreshPendingBugs]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-brand">
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(prev => !prev)}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={sidebarOpen}
            aria-controls="manager-navigation"
          >
            {sidebarOpen ? '✕' : '☰'}
          </button>
          <h1>MockMedic <span>Manager</span></h1>
        </div>
        <div className="user-info">
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div id="manager-navigation" className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <button className="sidebar-dismiss" onClick={() => setSidebarOpen(false)} aria-label="Close menu">Close menu ✕</button>
          <nav className="sidebar-nav">
            <button
              className={`nav-item ${currentPage === 'userManagement' ? 'active' : ''}`}
              onClick={() => {
                onPageChange('userManagement');
                setSidebarOpen(false);
              }}
            >
              👥 User Management
            </button>
            <button
              className={`nav-item ${currentPage === 'usage' ? 'active' : ''}`}
              onClick={() => {
                onPageChange('usage');
                setSidebarOpen(false);
              }}
            >
              🌍 Usage Analysis
            </button>
            <button
              className={`nav-item ${currentPage === 'interviewAnalysis' ? 'active' : ''}`}
              onClick={() => {
                onPageChange('interviewAnalysis');
                setSidebarOpen(false);
              }}
            >
              📊 Interview Analysis
            </button>
            <button
              className={`nav-item ${currentPage === 'questionBankAnalysis' ? 'active' : ''}`}
              onClick={() => {
                onPageChange('questionBankAnalysis');
                setSidebarOpen(false);
              }}
            >
              🗂️ QB Analysis
            </button>
            <button
              className={`nav-item ${currentPage === 'apiTest' ? 'active' : ''}`}
              onClick={() => {
                onPageChange('apiTest');
                setSidebarOpen(false);
              }}
            >
              🔧 API Test
            </button>
            <button
              className={`nav-item ${currentPage === 'bugReports' ? 'active' : ''} ${hasPendingBugs ? 'has-pending-bugs' : ''}`}
              onClick={() => {
                onPageChange('bugReports');
                setSidebarOpen(false);
              }}
            >
              🐛 Bug Reports{hasPendingBugs && <span className="pending-indicator"> ●</span>}
            </button>
          </nav>
        </div>
        {sidebarOpen && <div className="sidebar-overlay visible" onClick={() => setSidebarOpen(false)} />}

        <div className="main-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
